import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { Loader2, Wrench, Building2, Activity } from 'lucide-react';

const HOTEL_PALETTE = ['#3B82F6', '#A855F7', '#F59E0B', '#10B981', '#EC4899', '#06B6D4', '#EF4444', '#8B5CF6', '#F97316', '#14B8A6'];
const TECH_LOAD_COLOR = (count) => {
  // Soglie semplici: 0-1 verde, 2-4 ambra, 5+ rosso
  if (count >= 5) return '#EF4444';
  if (count >= 2) return '#F59E0B';
  return '#10B981';
};

/**
 * Card-Bloomberg style: lista compatta con barra di carico orizzontale.
 * Mantiene le funzionalità originali (click → navigate).
 */
const TacticalListCard = ({ moduleId, title, value, subtitle, items, icon: Icon, onItemClick, emptyMessage, colorMode = 'load' }) => {
  const maxCount = Math.max(1, ...items.map((it) => it.count || 0));

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-5">
        {/* Header tactical */}
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] tracking-widest text-slate-400 font-mono">
            {moduleId}
          </div>
          <Icon className="w-3.5 h-3.5 text-slate-400" />
        </div>
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200 mb-3">
          {title}
        </h3>

        {/* Big number */}
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-4xl font-black tabular-nums text-blue-600 dark:text-blue-400">{value}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</span>
        </div>

        {/* Item list with bars */}
        <div className="mt-4 space-y-2 border-t border-slate-100 dark:border-slate-800 pt-3 max-h-[280px] overflow-y-auto pr-1">
          {items.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-2">{emptyMessage || 'Nessun dato'}</p>
          ) : (
            items.map((it, idx) => {
              const count = it.count || 0;
              const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
              const barColor =
                colorMode === 'palette'
                  ? HOTEL_PALETTE[idx % HOTEL_PALETTE.length]
                  : TECH_LOAD_COLOR(count);
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onItemClick && onItemClick(it)}
                  disabled={!onItemClick}
                  className={`w-full flex items-center gap-3 px-2 py-1.5 rounded-md transition-colors text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 ${onItemClick ? 'cursor-pointer' : 'cursor-default'}`}
                  data-testid={`avg-row-${it.id}`}
                >
                  {/* Dot colorato */}
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: barColor, boxShadow: `0 0 6px ${barColor}99` }}
                  />
                  {/* Nome */}
                  <span className="text-sm text-slate-700 dark:text-slate-200 truncate flex-1 min-w-0 font-medium">
                    {it.name}
                  </span>
                  {/* Barra */}
                  <div className="w-24 h-1.5 rounded-full overflow-hidden shrink-0 bg-slate-100 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${pct}%`,
                        background: barColor,
                        boxShadow: `0 0 6px ${barColor}55`,
                      }}
                    />
                  </div>
                  {/* Valore */}
                  <span className="text-sm font-bold tabular-nums shrink-0 w-10 text-right" style={{ color: count === 0 ? undefined : barColor }}>
                    {count}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Footer mono */}
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] font-mono text-slate-400 dark:text-slate-500">
          <span>{items.length} entries</span>
          <span>● LIVE</span>
        </div>
      </CardContent>
    </Card>
  );
};

const DailyAveragesCard = ({ showHotels = true, extraCard = null }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/daily-averages')
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-blue-700" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  // Dedup hotels: rimuovi entry con nome duplicato (es. "Hotel Arcimboldi" presente 2 volte nel DB)
  const seenNames = new Set();
  const perHotelDedup = (data.per_hotel || []).filter((h) => {
    const key = (h.name || '').trim().toLowerCase();
    if (!key || seenNames.has(key)) return false;
    seenNames.add(key);
    return true;
  });

  return (
    <div className="space-y-4" data-testid="daily-averages">
      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Activity className="w-4 h-4" />
        <span className="font-mono text-xs">
          OGGI · <b className="text-slate-900 dark:text-slate-100">{data.tickets_today}</b> {data.tickets_today === 1 ? 'ticket aperto' : 'ticket aperti'}
        </span>
      </div>
      <div className={`grid grid-cols-1 gap-4 ${showHotels && extraCard ? 'lg:grid-cols-3' : (showHotels || extraCard) ? 'lg:grid-cols-2' : ''}`}>
        <TacticalListCard
          moduleId="LOAD_01 · TECH_DISTRIBUTION"
          title="Media per Tecnico"
          value={data.avg_per_technician.toFixed(1)}
          subtitle={`ticket/tecnico oggi (${data.technicians_count})`}
          items={data.per_technician || []}
          icon={Wrench}
          colorMode="load"
          onItemClick={(it) => navigate(`/tickets?assigned_to=${it.id}`)}
          emptyMessage="Nessun tecnico nei tuoi hotel"
        />
        {showHotels && (
          <TacticalListCard
            moduleId="LOAD_02 · HOTEL_DISTRIBUTION"
            title="Media per Hotel"
            value={data.avg_per_hotel.toFixed(1)}
            subtitle={`ticket/hotel oggi (${data.hotels_count})`}
            items={perHotelDedup}
            icon={Building2}
            colorMode="palette"
            onItemClick={(it) => navigate(`/tickets?hotel=${it.id}`)}
            emptyMessage="Nessun hotel"
          />
        )}
        {extraCard}
      </div>
    </div>
  );
};

export default DailyAveragesCard;
